/**
 * מפתח מרכזי: אובייקט בחירת הזמנה לפנייה לאופרציה (+ raw מוטען מהשרת) → תצוגת סוכן.
 */

import { resolvePickerOrderDisplayNumber } from "./pickerOrderDisplayResolver.js";
import { resolveOrderAmountDisplayForPicker } from "../../utils/orderAmountDisplayResolver.js";
import { getLdsOrderPrimaryBadgeText } from "../../utils/ldsOrderStatus.js";
import { resolveUsdIlsRateNumber } from "../../utils/orders/formatOrderPrice.js";
import { parseNumber } from "../../services/currencyService.js";

function str(v) {
  if (v == null) return "";
  return String(v).trim();
}

function parseMaybeJson(v) {
  if (v == null) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!t || (!t.startsWith("{") && !t.startsWith("["))) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function formatPersonName(nameBlock, fallbackPax) {
  if (nameBlock && typeof nameBlock === "object" && !Array.isArray(nameBlock)) {
    const parts = [
      str(nameBlock.NamePrefix ?? nameBlock.prefix),
      str(nameBlock.GivenName ?? nameBlock.given),
      str(nameBlock.Surname ?? nameBlock.surname),
    ].filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  const t = str(fallbackPax?.title);
  const f = str(fallbackPax?.firstname ?? fallbackPax?.firstName);
  const l = str(fallbackPax?.lastname ?? fallbackPax?.lastName);
  const joined = [t, f, l].filter(Boolean).join(" ");
  return joined || "—";
}

function extractPhonesFromPersonDetails(pd) {
  if (!pd || typeof pd !== "object") return [];
  const out = [];
  for (const k of ["Telephone", "telephone", "Phone", "phone", "Mobile", "mobile"]) {
    const v = pd[k];
    if (v == null || v === "") continue;
    if (typeof v === "object" && !Array.isArray(v)) {
      const n = str(v.PhoneNumber ?? v.number ?? v.Number);
      if (n) out.push(n);
      continue;
    }
    if (Array.isArray(v)) {
      for (const item of v) {
        const n =
          typeof item === "object" && item
            ? str(item.PhoneNumber ?? item.number)
            : str(item);
        if (n) out.push(n);
      }
      continue;
    }
    const s = str(v);
    if (s) out.push(s);
  }
  return out;
}

function extractEmailsFromPersonDetails(pd) {
  if (!pd || typeof pd !== "object") return [];
  const v = pd.Email ?? pd.email;
  if (!v) return [];
  if (typeof v === "object") return [str(v.Address ?? v.address)].filter(Boolean);
  return [str(v)].filter(Boolean);
}

function extractOldNameParts(pd, p) {
  const name = pd.Name ?? pd.name;
  if (name && typeof name === "object" && !Array.isArray(name)) {
    return {
      titlePrefix: str(name.NamePrefix ?? name.prefix),
      oldFirstName: str(name.GivenName ?? name.given),
      oldLastName: str(name.Surname ?? name.surname),
    };
  }
  return {
    titlePrefix: str(p.title),
    oldFirstName: str(p.firstname ?? p.firstName),
    oldLastName: str(p.lastname ?? p.lastName),
  };
}

function parseHotelMainContactTokens(maincontact) {
  const mc = str(maincontact);
  if (!mc) {
    return { titlePrefix: "", oldFirstName: "", oldLastName: "", displayName: "—" };
  }
  const tokens = mc.split(/\s+/).filter(Boolean);
  const titles = new Set(["MR", "MRS", "MS", "MISS", "MSTR", "DR", "PROF"]);
  let i = 0;
  let titlePrefix = "";
  if (tokens.length && titles.has(tokens[0]?.toUpperCase())) {
    titlePrefix = tokens[0];
    i = 1;
  }
  if (tokens.length - i >= 2) {
    return {
      titlePrefix,
      oldFirstName: tokens[i],
      oldLastName: tokens.slice(i + 1).join(" "),
      displayName: [titlePrefix, tokens[i], ...tokens.slice(i + 1)].filter(Boolean).join(" "),
    };
  }
  if (tokens.length - i === 1) {
    return {
      titlePrefix,
      oldFirstName: tokens[i] || "",
      oldLastName: "",
      displayName: tokens.join(" "),
    };
  }
  return { titlePrefix: "", oldFirstName: "", oldLastName: "", displayName: mc };
}

/**
 * @param {unknown} passengersinfo
 * @returns {Array<Record<string, unknown>>}
 */
export function flattenPassengersFromRaw(passengersinfo) {
  let pi = passengersinfo;
  if (typeof pi === "string") {
    pi = parseMaybeJson(pi);
  }
  if (!Array.isArray(pi)) return [];

  const out = [];
  let seq = 0;
  pi.forEach((room, roomIdx) => {
    if (!room || typeof room !== "object") return;
    const roomLabel = str(room.roomName ?? room.RoomName ?? `חדר ${roomIdx + 1}`);
    const slots = [
      ["adults", "מבוגר"],
      ["kids", "ילד"],
      ["children", "ילד"],
      ["infants", "תינוק"],
    ];
    for (const [slotKey, roleHe] of slots) {
      const list = room[slotKey];
      if (!Array.isArray(list)) continue;
      for (const p of list) {
        if (!p || typeof p !== "object") continue;
        seq += 1;
        const pd = p.PersonDetails ?? p.personDetails ?? {};
        const displayName = formatPersonName(pd.Name ?? pd.name, p);
        const phones = extractPhonesFromPersonDetails(pd);
        const emails = extractEmailsFromPersonDetails(pd);
        const { titlePrefix, oldFirstName, oldLastName } = extractOldNameParts(pd, p);
        out.push({
          id: seq,
          roomLabel,
          role: roleHe,
          slot: slotKey,
          displayName,
          titlePrefix,
          oldFirstName,
          oldLastName,
          phones,
          emails,
        });
      }
    }
  });
  return out;
}

function normalizePaymentsList(raw) {
  if (!raw || typeof raw !== "object") return [];
  let arr = raw.payments ?? raw.order_payments ?? raw.Payments;
  if (!Array.isArray(arr)) return [];
  return arr
    .map((p) => {
      if (p == null) return null;
      if (typeof p === "string") return parseMaybeJson(p) || null;
      return typeof p === "object" ? p : null;
    })
    .filter(Boolean);
}

export function inferPaymentMethodHe(p) {
  const hay = `${str(p?.type)} ${str(p?.paymentType)} ${str(p?.PaymentType)} ${str(p?.method)}`.toLowerCase();
  if (hay.includes("j5") || hay.includes("hyp") || hay.includes("meshulam")) return "תשלום מקוון / J5";
  if (p?.CardNumber || p?.cardNumber || hay.includes("credit") || hay.includes("card")) return "אשראי";
  if (hay.includes("transfer") || hay.includes("bank") || p?.bank_transfer) return "העברה בנקאית";
  if (hay.includes("link") || p?.payment_url || p?.PaymentUrl) return "לינק תשלום";
  return "אחר";
}

function paymentStatusHe(p) {
  if (p?.paid === true || p?.Paid === true || p?.paid === 1) return "שולם";
  if (p?.failed === true || p?.status === "failed") return "נכשל";
  if (p?.pending === true || str(p?.status).toLowerCase() === "pending") return "ממתין";
  return str(p?.status) || "—";
}

function mapPaymentRow(p, idx) {
  const amount = p.Amount ?? p.amount ?? p.AmountILS ?? p.AmountUSD ?? p.sum;
  const ccy = str(p.Currency ?? p.currency ?? "ILS").toUpperCase() || "ILS";
  const at =
    p.paidAt ??
    p.PaidAt ??
    p.date ??
    p.Date ??
    p.created_at ??
    p.createdAt ??
    p.transactionDate ??
    null;
  const conf =
    p.confirmationNumber ??
    p.ConfirmationNumber ??
    p.ApprovalNumber ??
    p.approvalNumber ??
    p.transactionId ??
    p.TransactionId ??
    p.uid ??
    null;
  const tash = p.tash ?? p.Tash ?? p.numberofpayments ?? p.numberOfPayments ?? null;
  return {
    key: idx,
    paidAt: at != null ? str(at) : "—",
    amount: amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null,
    currency: ccy,
    status: paymentStatusHe(p),
    confirmation: conf != null ? str(conf) : "—",
    method: inferPaymentMethodHe(p),
    installments: tash != null && str(tash) !== "" ? str(tash) : "—",
  };
}

function collectSpecialNotes(raw) {
  const parts = [];
  const push = (v) => {
    const s = str(v);
    if (s) parts.push(s);
  };
  push(raw.special_requests);
  push(raw.specialRequests);
  push(raw.remarks);
  push(raw.remark);
  push(raw.notes);
  push(raw.order_notes);
  push(raw.internal_notes);
  const or = parseMaybeJson(raw.order_response) ?? raw.order_response;
  if (or && typeof or === "object") {
    const sr = or.specialRequests ?? or.special_requests ?? or.remarks;
    if (sr) push(typeof sr === "string" ? sr : JSON.stringify(sr));
  }
  return parts;
}

function resolveOrderKindLabel(pickerRow, raw, sourceKind) {
  const cat = str(pickerRow?.category);
  const dt = str(raw?.dealtype ?? raw?.deal_type).toLowerCase();
  const sk = str(sourceKind);
  if (sk === "hotel" || cat === "מלונות") return "הזמנת מלון (Gimmonix)";
  if (sk === "dynamic" || cat.includes("דינמ")) return "חבילה דינמית";
  if (sk === "cruise") return "שייט";
  if (dt.includes("organized") || cat.includes("מאורגנ")) return "טיול מאורגן (LDS)";
  if (dt === "flights" || (dt.includes("flight") && !dt.includes("hotel"))) return "טיסות (LDS)";
  if (cat === "חבילות" || sk === "lds") return "חבילת נופש (LDS)";
  if (cat) return cat;
  return "הזמנה";
}

function mergeContactBook(passengers, raw, pickerRow) {
  const phones = new Set();
  const emails = new Set();
  for (const p of passengers) {
    (p.phones || []).forEach((x) => phones.add(str(x)));
    (p.emails || []).forEach((x) => emails.add(str(x)));
  }
  const mc = str(pickerRow?.mainContact ?? raw?.maincontact ?? raw?.main_contact);
  const extraPhone = str(raw?.phone ?? raw?.mobile ?? raw?.cell ?? raw?.telephone);
  const extraEmail = str(raw?.email ?? raw?.Email);
  if (extraPhone) phones.add(extraPhone);
  if (extraEmail) emails.add(extraEmail);
  return {
    primaryName: mc || null,
    phones: [...phones].filter(Boolean),
    emails: [...emails].filter(Boolean),
  };
}

function formatCreatedAtLabel(pickerRow, raw) {
  const v =
    raw?.orderdate ??
    raw?.order_date ??
    raw?.created_at ??
    raw?.createdAt ??
    pickerRow?.createdAt ??
    pickerRow?.created_at ??
    null;
  const s = str(v);
  return s || "—";
}

function resolveFinancialMeta(amountFacet, pickerRow, raw, appUsdIlsRate) {
  const t = amountFacet.trace || {};
  const decl = str(pickerRow?.currency ?? raw?.currency ?? "").toUpperCase();
  let sourceCurrencyLabel = "—";
  if (t.selectedDisplayCurrency === "BOTH") sourceCurrencyLabel = "ILS + USD";
  else if (t.selectedDisplayCurrency === "USD") sourceCurrencyLabel = "USD";
  else if (t.selectedDisplayCurrency === "ILS") sourceCurrencyLabel = "ILS";
  else if (decl) sourceCurrencyLabel = decl;

  const rateNum = resolveUsdIlsRateNumber(appUsdIlsRate);
  const exchangeRateLabel =
    rateNum != null ? `${parseNumber(rateNum)} ₪ לדולר (שער יציג / מערכת)` : "—";

  return { sourceCurrencyLabel, exchangeRateLabel };
}

function resolveSupplierDisplay(pickerRow, raw) {
  return {
    supplierName: str(raw?.supplier_name ?? raw?.SupplierName ?? "") || "Hotels River",
    contactName: str(
      raw?.contact_person ??
        raw?.contactPerson ??
        raw?.agent_name ??
        pickerRow?.agentFirstName ??
        pickerRow?.agentLastName ??
        ""
    ),
    phone: str(raw?.supplier_phone ?? raw?.phone ?? raw?.mobile ?? raw?.telephone ?? ""),
    email: str(raw?.supplier_email ?? raw?.email ?? raw?.Email ?? ""),
  };
}

function buildPaymentOverview(pickerRow, raw) {
  const pPaid = raw?.paid === true || raw?.paid === 1 || pickerRow?.paid === true;
  const bank = raw?.bank_transfer === true || raw?.bank_transfer === 1;
  let label = "—";
  if (raw && typeof raw === "object" && (raw.dealid != null || raw.dealtype != null)) {
    try {
      label = getLdsOrderPrimaryBadgeText(raw) || "—";
    } catch {
      label = pickerRow?.status || "—";
    }
  } else {
    label =
      pPaid ? "שולמה" : bank ? "ממתינה להעברה בנקאית" : str(pickerRow?.status || raw?.status || "ממתינה לתשלום");
  }
  return { paidFlag: Boolean(pPaid), bankTransfer: Boolean(bank), statusLine: label };
}

function dynamicPrimaryPassengers(raw) {
  const fn = str(raw?.order_firstname);
  const ln = str(raw?.order_lastname);
  if (!fn && !ln) return [];
  return [
    {
      id: 1,
      roomLabel: "—",
      role: "מבוגר",
      slot: "dynamic",
      displayName: [fn, ln].filter(Boolean).join(" "),
      titlePrefix: "",
      oldFirstName: fn,
      oldLastName: ln,
      phones: [],
      emails: [],
    },
  ];
}

function hotelPassengerFallback(raw) {
  const parsed = parseHotelMainContactTokens(raw?.maincontact ?? raw?.main_contact);
  if (parsed.displayName === "—") return [];
  return [
    {
      id: 1,
      roomLabel: "—",
      role: "מבוגר",
      slot: "hotel",
      displayName: parsed.displayName,
      titlePrefix: parsed.titlePrefix,
      oldFirstName: parsed.oldFirstName,
      oldLastName: parsed.oldLastName,
      phones: [],
      emails: [],
    },
  ];
}

/**
 * @param {Record<string, unknown>|null|undefined} pickerRow - שורת picker / מנורמלת + raw מוזג
 * @param {{ emitTrace?: boolean, usdIlsRateFromApp?: number|null|undefined }} [options]
 */
export function resolveOperationRequestOrderContext(pickerRow, options = {}) {
  const emitTrace = options.emitTrace === true;
  const raw =
    pickerRow?.raw && typeof pickerRow.raw === "object" && !Array.isArray(pickerRow.raw)
      ? pickerRow.raw
      : {};
  const sourceType =
    str(pickerRow?._enrichedSourceKind) ||
    str(pickerRow?.category) ||
    str(pickerRow?.rawOrderType) ||
    "unknown";

  let passengers = flattenPassengersFromRaw(raw.passengersinfo ?? raw.passengers_info);
  if (!passengers.length && str(pickerRow?.category).includes("דינמ")) {
    passengers = dynamicPrimaryPassengers(raw);
  }
  if (!passengers.length && (sourceType === "hotel" || pickerRow?.category === "מלונות")) {
    passengers = hotelPassengerFallback(raw);
  }

  const paymentsRaw = normalizePaymentsList(raw);
  const payments = paymentsRaw.map((p, i) => mapPaymentRow(p, i + 1));

  const amountFacet = resolveOrderAmountDisplayForPicker(pickerRow, { emitTrace: false });
  const appUsd = options.usdIlsRateFromApp;
  const finMeta = resolveFinancialMeta(amountFacet, pickerRow, raw, appUsd);
  const financials = {
    displayTotalText: amountFacet.displayTotalText,
    displayPaidText: amountFacet.displayPaidText,
    displayRemainingText: amountFacet.displayRemainingText,
    totalFacet: amountFacet.total,
    remainingFacet: amountFacet.remaining,
    sourceCurrencyLabel: finMeta.sourceCurrencyLabel,
    exchangeRateLabel: finMeta.exchangeRateLabel,
    amountResolutionTrace: amountFacet.trace,
  };

  const display = resolvePickerOrderDisplayNumber(pickerRow);
  const supplierDisplay = resolveSupplierDisplay(pickerRow, raw);
  const supplierInfo = {
    bookingRef: str(pickerRow?.bookingRef ?? raw.booking_ref ?? raw.bookingRef) || null,
    dealId: raw.dealid != null ? str(raw.dealid) : null,
    dealType: raw.dealtype != null ? str(raw.dealtype) : null,
    ldsOrderDbId: raw.id != null ? str(raw.id) : str(pickerRow?.id ?? pickerRow?.orderId),
    supplierName: supplierDisplay.supplierName,
    contactName: supplierDisplay.contactName || null,
    phone: supplierDisplay.phone || null,
    email: supplierDisplay.email || null,
  };

  const notes = collectSpecialNotes(raw);
  const warnings = [];
  if (raw._fromBookingRefLookup) {
    warnings.push("ההזמנה נטענה מחיפוש מספר נסיעה — הוספת פירוט מלא מהשרת.");
  }
  if (!passengers.length && (raw.passengersinfo || raw.passengers_info)) {
    warnings.push("נמצא מידע נוסעים בצורה לא צפויה — לא חולצו שמות.");
  }

  const orderSummary = {
    displayNumber: display.displayNumber,
    internalOrderId: display.internalOrderId,
    orderKindLabel: resolveOrderKindLabel(pickerRow, raw, pickerRow?._enrichedSourceKind),
    rawSourceType: str(pickerRow?.rawOrderType ?? pickerRow?.orderType ?? ""),
    status: str(pickerRow?.status ?? ""),
    destination: str(pickerRow?.hotelName ?? raw?.hotel_package_hotelname ?? ""),
    checkIn: str(pickerRow?.checkIn ?? raw?.checkin ?? raw?.dep_date ?? ""),
    checkOut: str(pickerRow?.checkOut ?? raw?.checkout ?? raw?.ret_date ?? ""),
    createdAtLabel: formatCreatedAtLabel(pickerRow, raw),
    contacts: mergeContactBook(passengers, raw, pickerRow),
    paymentOverview: buildPaymentOverview(pickerRow, raw),
    specialNotes: notes,
  };

  const trace = {
    orderId: pickerRow?.orderId ?? pickerRow?.id ?? null,
    displayOrderNumber: display.displayNumber,
    sourceType,
    passengersCount: passengers.length,
    passengers,
    paymentsCount: payments.length,
    payments,
    financials,
    rawKeys:
      raw && typeof raw === "object" && !Array.isArray(raw)
        ? Object.keys(raw)
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 120)
        : [],
  };

  if (emitTrace) {
    console.log("[OP_REQUEST_ORDER_CONTEXT_TRACE]", trace);
  }

  const traces = { orderContext: trace };

  return {
    orderSummary,
    passengers,
    payments,
    financials,
    supplierInfo,
    warnings,
    trace,
    traces,
  };
}
